
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Plus, Trash2, Settings2, ChevronDown, Info, X, Check } from "lucide-react";
import { makeId, INPUT_UNITS, clampNumber, TRANSPORT_MODES } from "../../lca/lcaTypes";
import CarbonIntensityLookup from "../CarbonIntensityLookup";

function UnitDropdown({ value, options, onChange, className = "" }) {
  const current = options.find((o) => o.key === value);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={`h-8 justify-between px-2 text-xs border-[#d5dacc] bg-white text-[#2b4331] hover:bg-[#eef2e7] ${className}`}
        >
          <span className="font-medium">{current?.label || value}</span>
          <ChevronDown className="w-4 h-4 text-[#4f6b5a]" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[140px]">
        {options.map((o) => (
          <DropdownMenuItem
            key={o.key}
            className="text-xs"
            onSelect={(e) => {
              e.preventDefault();
              onChange(o.key);
            }}
          >
            {o.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const COMPONENT_GRID = "sm:grid-cols-[220px_140px_90px_70px_100px_minmax(500px,1fr)_80px]";

function EnergyInputs({
  item,
  onAdd,
  onUpdate,
  onRemove,
  buildingArea,
  buildingLifeYears
}) {
  const [showLookup, setShowLookup] = useState(false);

  const energyUse = clampNumber(item?.energyUseKwhPerM2Yr, 0);
  const gridIntensity = clampNumber(item?.gridIntensityKgPerKwh, 0);
  const impactPerM2Yr = energyUse > 0 && gridIntensity > 0 ? energyUse * gridIntensity : null;
  const denomOk = clampNumber(buildingArea, 0) > 0 && clampNumber(buildingLifeYears, 0) > 0;
  const totalImpact = impactPerM2Yr != null && denomOk
    ? impactPerM2Yr * clampNumber(buildingArea, 0) * clampNumber(buildingLifeYears, 0)
    : null;

  if (!item) {
    return (
      <div className="flex flex-col gap-2">
        <div className="text-sm text-[#4f6b5a]">Add operational energy inputs to calculate B6 impacts.</div>
        <Button
          type="button"
          className="h-8 w-full sm:w-auto bg-[#2f6046] text-white hover:bg-[#25503a]"
          onClick={() =>
            onAdd({
              id: makeId("energy"),
              category: "energy",
              name: "Operational energy",
              subcategory: "B6",
              energyUseKwhPerM2Yr: "",
              gridIntensityKgPerKwh: "",
              gridIntensityYear: "",
              gridIntensitySource: ""
            })
          }
        >
          <Plus className="w-4 h-4 mr-1" />
          Add energy module
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#d5dacc] bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-[#2b4331]">Operational energy (B6)</div>
        <Button
          type="button"
          variant="ghost"
          className="h-8 px-2"
          onClick={() => onRemove(item.id)}
        >
          <Trash2 className="w-4 h-4 text-[#4f6b5a]" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1">
          <div className="text-xs text-[#4f6b5a]">Building consumption (kWh/m²/yr)</div>
          <Input
            value={item.energyUseKwhPerM2Yr ?? ""}
            onChange={(e) => onUpdate({ ...item, energyUseKwhPerM2Yr: e.target.value })}
            inputMode="decimal"
            className="h-8"
            placeholder="e.g., 120"
          />
        </div>
        <div className="space-y-1">
          <div className="text-xs text-[#4f6b5a]">Grid intensity (kgCO2e/kWh)</div>
          <div className="flex items-center gap-2">
            <Input
              value={item.gridIntensityKgPerKwh ?? ""}
              onChange={(e) => onUpdate({ ...item, gridIntensityKgPerKwh: e.target.value })}
              inputMode="decimal"
              className="h-8"
              placeholder="e.g., 0.45"
            />
            <Button
              type="button"
              variant="outline"
              className="h-8 border-[#d5dacc] bg-white text-[#2b4331] hover:bg-[#eef2e7]"
              onClick={() => setShowLookup(true)}
            >
              Lookup
            </Button>
          </div>
          {item.gridIntensitySource ? (
            <div className="text-[11px] text-[#4f6b5a]">
              {item.gridIntensitySource}
              {item.gridIntensityYear ? ` (${item.gridIntensityYear})` : ""}
            </div>
          ) : null}
        </div>
        <div className="space-y-1">
          <div className="text-xs text-[#4f6b5a]">Energy impact (kgCO2e/m²/yr)</div>
          <div className="h-8 flex items-center text-sm font-semibold text-[#2b4331]">
            {impactPerM2Yr != null ? impactPerM2Yr.toFixed(3) : "—"}
          </div>
          <div className="text-[11px] text-[#4f6b5a]">
            {totalImpact != null
              ? `Total over life: ${Math.round(totalImpact).toLocaleString()} kgCO2e`
              : "Set building area and life to see total."}
          </div>
        </div>
      </div>

      <Dialog open={showLookup} onOpenChange={setShowLookup}>
        <DialogContent className="flex w-full max-h-[90vh] max-w-[95vw] flex-col overflow-hidden p-0 sm:w-auto md:max-w-[90vw] lg:max-w-[1200px]">
          <div className="px-4 pt-4 sm:px-6 sm:pt-6">
            <DialogHeader className="space-y-2">
              <DialogTitle className="text-black">Grid carbon intensity lookup</DialogTitle>
              <DialogDescription className="text-black">
                Search Our World in Data for grid carbon intensity (gCO2/kWh) and apply the latest value to this analysis.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="flex-1 overflow-y-auto px-4 pb-4 sm:px-6 sm:pb-6">
            <CarbonIntensityLookup
              onApplyValue={({ country, year, valueKg }) => {
                if (!Number.isFinite(valueKg)) return;
                const rounded = Number(valueKg.toFixed(3));
                const countryName = country?.name || "dataset";
                const countryCode = country?.code ? ` (${country.code})` : "";
                onUpdate({
                  ...item,
                  gridIntensityKgPerKwh: rounded.toFixed(3),
                  gridIntensityYear: year || "",
                  gridIntensitySource: `${countryName}${countryCode}`
                });
                setShowLookup(false);
              }}
              className="mt-4"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ComponentRow({
  item,
  epdLibrary,
  onUpdate,
  onRemove,
  computed,
  buildingArea,
  buildingLifeYears
}) {
  const [showActions, setShowActions] = useState(false);
  const [showTransport, setShowTransport] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const epd = useMemo(() => {
    return (epdLibrary || []).find((e) => e.id === item.epdId) || null;
  }, [epdLibrary, item.epdId]);

  const conversionOk = computed?.conversion?.ok;
  const missing = computed?.missing || [];
  const total = clampNumber(computed?.total, 0);
  const transport = item.transport || { distanceKm: "", legs: [] };
  const inputMeta = computed?.inputMeta || null;
  const needsConversion = (item?.quantityUnit || "m3") !== (epd?.declaredUnit || "");

  const addLeg = () => {
    const legs = Array.isArray(transport.legs) ? transport.legs : [];
    onUpdate({
      ...item,
      transport: { ...transport, legs: [...legs, { mode: "", km: "" }] }
    });
  };

  const updateLeg = (idx, patch) => {
    const legs = Array.isArray(transport.legs) ? [...transport.legs] : [];
    if (!legs[idx]) return;
    legs[idx] = { ...legs[idx], ...patch };
    onUpdate({ ...item, transport: { ...transport, legs } });
  };

  const removeLeg = (idx) => {
    const legs = Array.isArray(transport.legs) ? [...transport.legs] : [];
    legs.splice(idx, 1);
    onUpdate({ ...item, transport: { ...transport, legs } });
  };

  const totalLegKm = (transport.legs || []).reduce((sum, leg) => sum + clampNumber(leg?.km, 0), 0);
  const overBudget = clampNumber(transport.distanceKm, 0) > 0 && totalLegKm > clampNumber(transport.distanceKm, 0);
  const totalDistanceKm = clampNumber(transport.distanceKm, 0);
  const hasTotalDistance = totalDistanceKm > 0;
  const denom = (() => {
    const area = clampNumber(buildingArea, 0);
    const life = clampNumber(buildingLifeYears, 0);
    return area > 0 && life > 0 ? area * life : null;
  })();
  const a4TransportImpactKg = computed?.transportImpact;
  const transportMissing = computed?.transportMissing || [];
  const a4TransportPerM2Yr =
    denom && a4TransportImpactKg != null ? clampNumber(a4TransportImpactKg, 0) / denom : null;

  useEffect(() => {
    if (!showTransport) return;
    const legs = Array.isArray(transport.legs) ? transport.legs : [];
    if (legs.length > 0) return;
    onUpdate({
      ...item,
      transport: { ...transport, legs: [{ mode: "", km: "" }] }
    });
  }, [showTransport, transport, item, onUpdate]);

  const massCalcInfo = `
Tonne-km mass basis:
- If EPD declared unit is kg or t: use declared quantity directly.
- If declared unit is m3: mass = declared quantity * density.
- If declared unit is m2: mass = declared quantity * thickness * density.
- Otherwise: fall back to input quantity using the same rules.
`;

  const isRhinoItem = item?.source === "rhino";

  return (
    <div className="rounded-xl border border-[#d5dacc] bg-white p-3 space-y-2 shadow-sm">
      <div className={`flex flex-col gap-2 sm:grid ${COMPONENT_GRID} sm:items-center sm:gap-1`}>
        <Input
          value={item.name}
          onChange={(e) => onUpdate({ ...item, name: e.target.value })}
          aria-label="Component name"
          className={`h-8 w-full ${isRhinoItem ? "bg-[#f3f6f1] text-[#2b4331]" : ""}`}
          readOnly={isRhinoItem}
        />

        <Input
          value={item.subcategory || ""}
          onChange={(e) => onUpdate({ ...item, subcategory: e.target.value })}
          aria-label="Subcategory"
          className="h-8 w-full"
        />

        <Input
          value={item.quantity ?? ""}
          onChange={(e) => onUpdate({ ...item, quantity: e.target.value })}
          aria-label="Quantity"
          className={`h-8 w-full ${isRhinoItem ? "bg-[#f3f6f1] text-[#2b4331]" : ""}`}
          inputMode="decimal"
          readOnly={isRhinoItem}
        />
        {isRhinoItem ? (
          <Button
            type="button"
            variant="outline"
            disabled
            className="h-8 w-full justify-between px-2 text-xs border-[#d5dacc] bg-[#f3f6f1] text-[#2b4331]"
          >
            <span className="font-medium">{item.quantityUnit || "m3"}</span>
            <ChevronDown className="w-4 h-4 text-[#4f6b5a]" />
          </Button>
        ) : (
          <UnitDropdown
            value={item.quantityUnit || "m3"}
            options={INPUT_UNITS}
            onChange={(unit) => onUpdate({ ...item, quantityUnit: unit })}
            className="w-full"
          />
        )}

        <Input
          value={item.serviceLifeYears ?? ""}
          onChange={(e) => onUpdate({ ...item, serviceLifeYears: e.target.value })}
          aria-label="Service life years"
          className="h-8 w-full"
          inputMode="decimal"
        />

        <div className="w-full min-w-0">
          <div className="relative">
            <select
              className="h-8 w-full appearance-none rounded-md border border-[#d5dacc] bg-white px-2 pr-10 text-sm text-[#2b4331]"
              value={item.epdId || ""}
              onChange={(e) => onUpdate({ ...item, epdId: e.target.value || null })}
            >
              <option value="">Select EPD</option>
              {(epdLibrary || []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} ({e.declaredUnit})
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4f6b5a]" />
          </div>
        </div>

        <div className="w-full flex items-center justify-end gap-1">
          <Button
            type="button"
            variant="outline"
            className="h-8 px-2 border-[#d5dacc] text-[#2b4331] bg-white hover:bg-[#eef2e7]"
            onClick={() => setShowActions(true)}
          >
            <Settings2 className="w-4 h-4 text-[#4f6b5a]" />
          </Button>

          <Popover open={confirmOpen} onOpenChange={setConfirmOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="h-8 px-2"
              >
                <Trash2 className="w-4 h-4 text-[#4f6b5a]" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="center"
              sideOffset={6}
              className="w-auto border border-[#d5dacc] bg-white text-[#2b4331] shadow-md px-2 py-1.5"
            >
              <div className="flex items-center gap-2 text-xs font-semibold">
                <span>Confirm?</span>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-[#2b4331] hover:bg-transparent hover:text-[#1f3528]"
                  onClick={() => setConfirmOpen(false)}
                  aria-label="Cancel delete"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-[#2f6046] hover:bg-transparent hover:text-[#214332]"
                  onClick={() => {
                    onRemove(item.id);
                    setConfirmOpen(false);
                  }}
                  aria-label="Confirm delete"
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <Dialog open={showActions} onOpenChange={setShowActions}>
      <DialogContent className="max-w-lg text-[#2b4331]">
        <DialogHeader>
          <DialogTitle className="text-[#2b4331]">Component actions</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-[#2b4331]">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <Button
              type="button"
              variant="outline"
              className="h-8 px-3 border-[#d5dacc] text-[#2b4331] bg-white hover:bg-[#eef2e7]"
              onClick={() => setShowTransport(true)}
            >
              A4 impact
            </Button>
            {a4TransportPerM2Yr != null ? (
              <div className="text-sm font-semibold text-[#2b4331] sm:ml-2">
                {a4TransportPerM2Yr.toFixed(3)} kgCO2e/m²/yr added for transport.
              </div>
            ) : a4TransportImpactKg != null ? (
              <div className="text-sm font-semibold text-[#2b4331] sm:ml-2">
                {Math.round(a4TransportImpactKg).toLocaleString()} kgCO2e added for transport.
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="space-y-1">
              <div className="text-xs text-[#2b4331]">
                Density (kg per m<sup>3</sup>)
              </div>
              <Input
                value={item.densityKgPerM3 ?? ""}
                onChange={(e) => onUpdate({ ...item, densityKgPerM3: e.target.value })}
                placeholder="Example 2400"
                className="h-8"
                inputMode="decimal"
              />
            </div>

            <div className="space-y-1">
              <div className="text-xs text-[#2b4331]">Thickness (m)</div>
              <Input
                value={item.thicknessM ?? ""}
                onChange={(e) => onUpdate({ ...item, thicknessM: e.target.value })}
                placeholder="Example 0.12"
                className="h-8"
                inputMode="decimal"
              />
            </div>

            <div className="space-y-1">
              <div className="text-xs text-[#2b4331]">Custom factor</div>
              <Input
                value={item.customFactor ?? ""}
                onChange={(e) => onUpdate({ ...item, customFactor: e.target.value })}
                placeholder="Fallback multiplier"
                className="h-8"
                inputMode="decimal"
              />
            </div>

            <div className="md:col-span-3 text-xs text-[#2b4331]">
              Conversion uses density and thickness when needed. Custom factor is a fallback multiplier when conversion is not supported.
            </div>
          </div>
        </div>
        </DialogContent>
      </Dialog>

      {conversionOk &&
        needsConversion &&
        (inputMeta?.usedEpdDensity ||
          inputMeta?.usedEpdThickness ||
          inputMeta?.usedGenericDensity ||
          inputMeta?.usedEpdArealDensity ||
          inputMeta?.usedGenericArealDensity) && (
        <div className="text-xs text-[#4f6b5a]">
          {inputMeta?.usedGenericDensity ? (
            <span>
              Using generic density:{" "}
              <span className="font-semibold text-[#2b4331]">{inputMeta.densityKgPerM3}</span> kg/m<sup>3</sup>.{" "}
              Edit if needed.
            </span>
          ) : null}
          {inputMeta?.usedGenericDensity &&
          (inputMeta?.usedEpdDensity ||
            inputMeta?.usedEpdThickness ||
            inputMeta?.usedEpdArealDensity ||
            inputMeta?.usedGenericArealDensity) ? (
            <span> · </span>
          ) : null}
          {inputMeta?.usedGenericArealDensity ? (
            <span>
              Using generic areal density:{" "}
              <span className="font-semibold text-[#2b4331]">{inputMeta.massPerAreaKgPerM2}</span> kg/m<sup>2</sup>.{" "}
              Edit if needed.
            </span>
          ) : null}
          {inputMeta?.usedGenericArealDensity &&
          (inputMeta?.usedEpdDensity || inputMeta?.usedEpdThickness || inputMeta?.usedEpdArealDensity) ? (
            <span> · </span>
          ) : null}
          {inputMeta?.usedEpdDensity ? (
            <span>
              Using EPD density: <span className="font-semibold text-[#2b4331]">{inputMeta.densityKgPerM3}</span>{" "}
              kg/m<sup>3</sup>
            </span>
          ) : null}
          {inputMeta?.usedEpdDensity &&
          (inputMeta?.usedEpdThickness || inputMeta?.usedEpdArealDensity) ? (
            <span> · </span>
          ) : null}
          {inputMeta?.usedEpdThickness ? (
            <span>
              Using EPD thickness: <span className="font-semibold text-[#2b4331]">{inputMeta.thicknessM}</span> m
            </span>
          ) : null}
          {inputMeta?.usedEpdThickness && inputMeta?.usedEpdArealDensity ? <span> · </span> : null}
          {inputMeta?.usedEpdArealDensity ? (
            <span>
              Using EPD areal density:{" "}
              <span className="font-semibold text-[#2b4331]">{inputMeta.massPerAreaKgPerM2}</span> kg/m<sup>2</sup>
            </span>
          ) : null}
        </div>
      )}

      {epd && computed?.ok && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#4f6b5a]">
          <span>
            Declared qty:{" "}
            <span className="font-semibold text-[#2b4331]">
              {computed?.conversion?.declaredQuantity?.toFixed?.(3) ?? computed?.conversion?.declaredQuantity}
            </span>
          </span>
          <span>
            Installs: <span className="font-semibold text-[#2b4331]">{computed?.installations}</span>
          </span>
          <span className="ml-auto">
            Total: <span className="font-semibold text-[#2b4331]">{Math.round(total).toLocaleString()} kgCO2e</span>
          </span>
        </div>
      )}

      {missing.length > 0 && (
        <div className="text-xs text-amber-700">
          Missing: {missing.join(", ")}
        </div>
      )}

      <Dialog open={showTransport} onOpenChange={setShowTransport}>
        <DialogContent className="w-full max-w-[calc(100vw-1rem)] sm:max-w-lg lg:max-w-xl bg-white text-[#2b4331] p-3 sm:p-6 max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader className="text-center">
            <DialogTitle className="text-[#2b4331] text-center w-full">A4 transport</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-[#4f6b5a] text-center sm:text-left">Total distance (km)</label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <Input
                  value={transport.distanceKm ?? ""}
                  onChange={(e) =>
                    onUpdate({ ...item, transport: { ...transport, distanceKm: e.target.value } })
                  }
                  inputMode="decimal"
                  className="h-8 w-full max-w-[260px] text-center sm:text-left"
                />
                <Button
                  type="button"
                  className="h-8 bg-[#2f6046] text-white hover:bg-[#25503a]"
                  onClick={() => setShowTransport(false)}
                >
                  Save
                </Button>
              </div>
              <p className="text-xs text-[#4f6b5a] text-center sm:text-left">
                Distance between production and site.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2 text-sm font-medium text-[#2b4331]">
                  <span>Transport legs</span>
                  <span className="text-[11px] text-[#4f6b5a]" title={massCalcInfo.trim()}>
                    <Info className="w-4 h-4 text-[#2f6046]" />
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="h-8 px-3 text-xs border-[#d5dacc] bg-white text-[#2b4331] hover:bg-[#eef2e7] ml-auto"
                  onClick={addLeg}
                >
                  Add leg
                </Button>
              </div>

              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {(transport.legs || []).length === 0 ? (
                  <div className="text-xs text-[#4f6b5a]">Add one or more legs with mode and km split.</div>
                ) : (
                  transport.legs.map((leg, idx) => {
                    const selectedMode = TRANSPORT_MODES.find((mode) => mode.key === leg.mode);
                    return (
                      <div
                        key={`${leg.mode || "leg"}-${idx}`}
                        className="rounded-lg border border-[#d5dacc] bg-[#f5f7f2] p-2 sm:p-3 space-y-2"
                      >
                        <div className="flex items-center gap-2 text-sm font-semibold text-[#2b4331]">
                          <span>Mode {idx + 1}</span>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-full p-1 text-[#4f6b5a] hover:text-red-600 hover:bg-white"
                            aria-label={`Remove mode ${idx + 1}`}
                            onClick={() => removeLeg(idx)}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="space-y-1 flex-1 min-w-0">
                            <label className="text-xs text-[#4f6b5a]">
                              <span className="sm:hidden">Mode</span>
                              <span className="hidden sm:inline">Mode type</span>
                            </label>
                            <Select
                              value={leg.mode || undefined}
                              onValueChange={(value) => updateLeg(idx, { mode: value })}
                            >
                              <SelectTrigger className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm border-[#d5dacc] bg-white text-[#2b4331]">
                                <SelectValue placeholder="Select a mode of transport" />
                              </SelectTrigger>
                              <SelectContent className="border border-[#d5dacc] bg-white text-[#2b4331]" align="start">
                                {TRANSPORT_MODES.map((mode) => (
                                  <SelectItem key={mode.key} value={mode.key} className="text-sm text-[#2b4331]">
                                    {mode.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {selectedMode ? (
                              <div className="text-[11px] text-[#4f6b5a]">
                                Impact: {selectedMode.factorKgPerTkm} kgCO2e/t-km
                              </div>
                            ) : null}
                          </div>
                          <div className="space-y-1 w-[28%] max-w-[110px] min-w-[60px] flex-shrink-0">
                            <label className="text-xs text-[#4f6b5a] text-right sm:text-left">
                              <span className="sm:hidden">km</span>
                              <span className="hidden sm:inline">Distance (km)</span>
                            </label>
                            <Input
                              value={leg.km ?? ""}
                              onChange={(e) => updateLeg(idx, { km: e.target.value })}
                              inputMode="decimal"
                              className="h-7 sm:h-8 w-full text-left text-sm"
                              disabled={!hasTotalDistance}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {!hasTotalDistance ? (
                <div className="text-xs text-amber-700">
                  Enter the total distance first to split km across transport legs.
                </div>
              ) : null}
              {overBudget ? (
                <div className="text-xs text-red-700">
                  Total split ({totalLegKm} km) exceeds total distance ({totalDistanceKm} km).
                </div>
              ) : null}
            </div>

            <div className="text-xs text-[#4f6b5a]">
              Transport emissions are added to module A4 using UK Government GHG Conversion Factors (freight, 2023/24). Legs beyond the total distance are ignored.
            </div>

            {a4TransportPerM2Yr != null ? (
              <div className="text-sm font-semibold text-[#2b4331] text-center">
                {a4TransportPerM2Yr.toFixed(3)} kgCO2e/m²/yr added for transport.
              </div>
            ) : a4TransportImpactKg != null ? (
              <div className="text-sm font-semibold text-[#2b4331] text-center">
                {Math.round(a4TransportImpactKg).toLocaleString()} kgCO2e added for transport.
              </div>
            ) : transportMissing.length > 0 ? (
              <div className="text-xs font-semibold text-red-700">
                Transport impact needs: {transportMissing.join(", ")}.
              </div>
            ) : (
              <div className="text-xs text-[#4f6b5a]">
                Transport impact needs distance and legs.
              </div>
            )}



          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CategorySection({
  categoryKey,
  categoryLabel,
  items,
  epdLibrary,
  computedByItemId,
  buildingArea,
  buildingLifeYears,
  onAdd,
  onUpdate,
  onRemove
}) {
  const sorted = useMemo(() => {
    const arr = [...(items || [])];
    arr.sort((a, b) => {
      const sa = (a.subcategory || "").toLowerCase();
      const sb = (b.subcategory || "").toLowerCase();
      if (sa < sb) return -1;
      if (sa > sb) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
    return arr;
  }, [items]);

  const addDefault = () => {
    onAdd({
      id: makeId("comp"),
      category: categoryKey,
      subcategory: "",
      name: "",
      quantity: "",
      quantityUnit: "m3",
      serviceLifeYears: "",
      densityKgPerM3: "",
      thicknessM: "",
      customFactor: "",
      epdId: "",
      transport: {
        distanceKm: "",
        legs: []
      }
    });
  };

  if (categoryKey === "energy") {
    const energyItem = (items || [])[0] || null;
    return (
      <Card className="border border-[#d5dacc] bg-white shadow-sm rounded-2xl">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-base text-[#2b4331]">{categoryLabel}</CardTitle>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          <EnergyInputs
            item={energyItem}
            onAdd={onAdd}
            onUpdate={onUpdate}
            onRemove={onRemove}
            buildingArea={buildingArea}
            buildingLifeYears={buildingLifeYears}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-[#d5dacc] bg-white shadow-sm rounded-2xl">
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="text-base text-[#2b4331]">{categoryLabel}</CardTitle>
          <Button
            type="button"
            className="h-8 w-full sm:w-auto bg-[#2f6046] text-white hover:bg-[#25503a]"
            onClick={addDefault}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add component
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {(sorted || []).length === 0 ? (
          <div className="text-sm text-[#4f6b5a]">
            No components added yet.
          </div>
        ) : (
          <>
            <div className={`hidden sm:grid ${COMPONENT_GRID} items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-[#4f6b5a] pl-4`}>
              <div>Component name</div>
              <div>Subcategory</div>
              <div>Qty</div>
              <div>Unit</div>
              <div>Service life</div>
              <div>EPD</div>
              <div />
            </div>
            {sorted.map((item) => (
              <ComponentRow
                key={item.id}
                item={item}
                epdLibrary={epdLibrary}
                computed={computedByItemId?.[item.id]}
                buildingArea={buildingArea}
                buildingLifeYears={buildingLifeYears}
                onUpdate={onUpdate}
                onRemove={onRemove}
              />
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}
